import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Breadcrumbs,
  Link,
  Button,
  TextField,
  TablePagination,
  CircularProgress,
  Alert,
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import api from "../api/api";

/* =======================
   TYPES
======================= */

type Device = {
  deviceId: string;
};

type Session = {
  _id: string;
  date: string;
};

type AuditLog = {
  text: string;
};

type ViewType = "devices" | "sessions" | "logs";

type LoadingState = {
  devices: boolean;
  sessions: boolean;
  logs: boolean;
};

type ErrorState = {
  devices: string | null;
  sessions: string | null;
  logs: string | null;
};

/* =======================
   CONSTANTS
======================= */

const LOG_REFRESH_INTERVAL = 5000;
const DEFAULT_ROWS_PER_PAGE = 25;
const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

/* =======================
   COMPONENT
======================= */

const Audit: React.FC = () => {
  // View state
  const [view, setView] = useState<ViewType>("devices");
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);

  // Data state
  const [devices, setDevices] = useState<Device[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [logs, setLogs] = useState<AuditLog[]>([]);

  // UI state
  const [loading, setLoading] = useState<LoadingState>({
    devices: false,
    sessions: false,
    logs: false,
  });
  const [errors, setErrors] = useState<ErrorState>({
    devices: null,
    sessions: null,
    logs: null,
  });

  // Search state
  const [deviceSearch, setDeviceSearch] = useState("");
  const [sessionSearch, setSessionSearch] = useState("");

  // Pagination state
  const [devicePage, setDevicePage] = useState(0);
  const [deviceRowsPerPage, setDeviceRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [sessionPage, setSessionPage] = useState(0);
  const [sessionRowsPerPage, setSessionRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  // Refs
  const logsEndRef = useRef<HTMLDivElement>(null);
  const isAtBottomRef = useRef(true);

  /* =======================
     FETCHERS
  ======================= */

  const fetchDevices = useCallback(async () => {
    setLoading((prev) => ({ ...prev, devices: true }));
    setErrors((prev) => ({ ...prev, devices: null }));

    try {
      const res = await api.get("/api/audit/devices");
      setDevices(res.data || []);
    } catch (error) {
      setDevices([]);
      setErrors((prev) => ({
        ...prev,
        devices: "Failed to load devices. Please try again."
      }));
    } finally {
      setLoading((prev) => ({ ...prev, devices: false }));
    }
  }, []);

  const fetchSessions = useCallback(async (deviceId: string) => {
    setLoading((prev) => ({ ...prev, sessions: true }));
    setErrors((prev) => ({ ...prev, sessions: null }));

    try {
      const res = await api.get(`/api/audit/devices/${deviceId}/sessions`);
      setSessions(res.data || []);
    } catch (error) {
      setSessions([]);
      setErrors((prev) => ({
        ...prev,
        sessions: "Failed to load sessions. Please try again."
      }));
    } finally {
      setLoading((prev) => ({ ...prev, sessions: false }));
    }
  }, []);

  const fetchLogs = useCallback(async (sessionId: string) => {
    setLoading((prev) => ({ ...prev, logs: true }));
    setErrors((prev) => ({ ...prev, logs: null }));

    try {
      const res = await api.get(`/api/audit/sessions/${sessionId}/logs`);
      setLogs(res.data || []);
    } catch (error) {
      setLogs([]);
      setErrors((prev) => ({
        ...prev,
        logs: "Failed to load logs. Please try again."
      }));
    } finally {
      setLoading((prev) => ({ ...prev, logs: false }));
    }
  }, []);

  /* =======================
     EFFECTS
  ======================= */

  // Fetch devices when returning to devices view
  useEffect(() => {
    if (view === "devices") {
      fetchDevices();
    }
  }, [view, fetchDevices]);

  // Auto-refresh logs
  useEffect(() => {
    if (view !== "logs" || !selectedSession) return;

    fetchLogs(selectedSession._id);
    const interval = setInterval(
      () => fetchLogs(selectedSession._id),
      LOG_REFRESH_INTERVAL
    );

    return () => clearInterval(interval);
  }, [view, selectedSession, fetchLogs]);

  // Scroll on view change (Initial load)
  useEffect(() => {
    if (view === "logs") {
      isAtBottomRef.current = true;
      // Small timeout to ensure rendering is complete
      setTimeout(() => {
        logsEndRef.current?.scrollIntoView();
      }, 0);
    }
  }, [view]);

  // Scroll on new logs (only if at bottom)
  useEffect(() => {
    if (view === "logs" && isAtBottomRef.current) {
      logsEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [logs, view]);

  /* =======================
     HANDLERS
  ======================= */

  const handleLogScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const atBottom = scrollHeight - scrollTop - clientHeight < 50;
    isAtBottomRef.current = atBottom;
  };

  const handleDeviceClick = useCallback((device: Device) => {
    setSelectedDevice(device);
    setSelectedSession(null);
    setSessionPage(0);
    setSessionSearch("");
    fetchSessions(device.deviceId);
    setView("sessions");
  }, [fetchSessions]);

  const handleSessionClick = useCallback((session: Session) => {
    setSelectedSession(session);
    setView("logs");
  }, []);

  const handleBack = useCallback(() => {
    if (view === "logs") {
      setView("sessions");
      setSelectedSession(null);
    } else {
      setView("devices");
      setSelectedDevice(null);
      setSelectedSession(null);
      setDevicePage(0);
      setDeviceSearch("");
    }
  }, [view]);

  const handleExportLogs = useCallback(() => {
    if (!selectedDevice || !selectedSession || logs.length === 0) return;

    const content = logs.map((log) => log.text).join("\n");
    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);

    const filename = `${selectedDevice.deviceId}_${selectedSession.date}_logs.txt`;
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [selectedDevice, selectedSession, logs]);

  const handleDeviceSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setDeviceSearch(e.target.value);
    setDevicePage(0);
  }, []);

  const handleSessionSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSessionSearch(e.target.value);
    setSessionPage(0);
  }, []);

  /* =======================
     FILTERED DATA
  ======================= */

  const filteredDevices = devices.filter((d) =>
    d.deviceId.toLowerCase().includes(deviceSearch.toLowerCase())
  );

  const filteredSessions = sessions.filter((s) =>
    s.date.includes(sessionSearch)
  );

  /* =======================
     RENDER HELPERS
  ======================= */

  const renderBreadcrumbs = () => (
    <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 3 }}>
      <Link
        underline="hover"
        sx={{ cursor: "pointer" }}
        onClick={() => {
          setView("devices");
          setSelectedDevice(null);
          setSelectedSession(null);
        }}
      >
        Devices
      </Link>

      {selectedDevice && (
        <Link
          underline="hover"
          sx={{ cursor: "pointer" }}
          onClick={() => {
            setView("sessions");
            setSelectedSession(null);
          }}
        >
          {selectedDevice.deviceId}
        </Link>
      )}

      {selectedSession && (
        <Typography color="text.primary">{selectedSession.date}</Typography>
      )}
    </Breadcrumbs>
  );

  const renderDevicesView = () => (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Devices
      </Typography>

      {errors.devices && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errors.devices}
        </Alert>
      )}

      <TextField
        label="Search IMEI"
        size="small"
        sx={{ mb: 2, width: 300 }}
        value={deviceSearch}
        onChange={handleDeviceSearchChange}
      />

      {loading.devices ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width={80}>Sr. No.</TableCell>
                  <TableCell>Device ID (IMEI)</TableCell>
                  <TableCell width={150}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDevices
                  .slice(
                    devicePage * deviceRowsPerPage,
                    devicePage * deviceRowsPerPage + deviceRowsPerPage
                  )
                  .map((device, index) => (
                    <TableRow key={device.deviceId} hover>
                      <TableCell>{devicePage * deviceRowsPerPage + index + 1}</TableCell>
                      <TableCell>{device.deviceId}</TableCell>
                      <TableCell>
                        <Link
                          sx={{ cursor: "pointer" }}
                          onClick={() => handleDeviceClick(device)}
                        >
                          View Logs
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}

                {filteredDevices.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      {deviceSearch ? "No devices match your search" : "No devices found"}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filteredDevices.length}
            page={devicePage}
            onPageChange={(_, p) => setDevicePage(p)}
            rowsPerPage={deviceRowsPerPage}
            onRowsPerPageChange={(e) => {
              setDeviceRowsPerPage(parseInt(e.target.value, 10));
              setDevicePage(0);
            }}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          />
        </>
      )}
    </Paper>
  );

  const renderSessionsView = () => (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Logs for {selectedDevice?.deviceId}
      </Typography>

      {errors.sessions && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errors.sessions}
        </Alert>
      )}

      <TextField
        label="Search Date (YYYY-MM-DD)"
        size="small"
        sx={{ mb: 2, width: 300 }}
        value={sessionSearch}
        onChange={handleSessionSearchChange}
      />

      {loading.sessions ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSessions
                  .slice(
                    sessionPage * sessionRowsPerPage,
                    sessionPage * sessionRowsPerPage + sessionRowsPerPage
                  )
                  .map((session) => (
                    <TableRow key={session._id} hover>
                      <TableCell>
                        <Link
                          sx={{ cursor: "pointer" }}
                          onClick={() => handleSessionClick(session)}
                        >
                          {session.date}
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}

                {filteredSessions.length === 0 && (
                  <TableRow>
                    <TableCell align="center">
                      {sessionSearch ? "No sessions match your search" : "No audit dates found"}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filteredSessions.length}
            page={sessionPage}
            onPageChange={(_, p) => setSessionPage(p)}
            rowsPerPage={sessionRowsPerPage}
            onRowsPerPageChange={(e) => {
              setSessionRowsPerPage(parseInt(e.target.value, 10));
              setSessionPage(0);
            }}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          />
        </>
      )}
    </Paper>
  );

  const renderLogsView = () => (
    <Paper sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h6">Logs for {selectedSession?.date}</Typography>

        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={handleExportLogs}
          disabled={logs.length === 0 || loading.logs}
        >
          Export Logs
        </Button>
      </Box>

      {errors.logs && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errors.logs}
        </Alert>
      )}

      {loading.logs && logs.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box
          onScroll={handleLogScroll}
          sx={{
            maxHeight: 500,
            overflow: "auto",
            backgroundColor: "#000",
            color: "#0f0",
            fontFamily: "monospace",
            fontSize: 13,
            p: 2,
            borderRadius: 1,
            position: "relative",
          }}
        >
          {logs.length === 0 ? (
            <Typography sx={{ color: "#0f0" }}>No logs available</Typography>
          ) : (
            logs.map((log, idx) => <div key={idx}>{log.text}</div>)
          )}
          <div ref={logsEndRef} />

          {loading.logs && logs.length > 0 && (
            <Box
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                backgroundColor: "rgba(0, 0, 0, 0.7)",
                p: 0.5,
                borderRadius: 1,
              }}
            >
              <CircularProgress size={16} sx={{ color: "#0f0" }} />
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );

  /* =======================
     MAIN RENDER
  ======================= */

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        {view !== "devices" && (
          <Button startIcon={<ArrowBackIcon />} onClick={handleBack}>
            Back
          </Button>
        )}
        <Typography variant="h4" fontWeight={700}>
          Device History
        </Typography>
      </Box>

      {/* Breadcrumbs */}
      {renderBreadcrumbs()}

      {/* Content */}
      {view === "devices" && renderDevicesView()}
      {view === "sessions" && selectedDevice && renderSessionsView()}
      {view === "logs" && selectedSession && renderLogsView()}
    </Box>
  );
};

export default Audit;